import { FreeGiftRule } from '@emico-hooks/cart-fragment'
import { useIsLoggedIn } from '@emico-hooks/login-token'
import { Plural, Trans } from '@lingui/macro'
import { Fragment } from 'react'

import styles from './FreeGifts.module.scss'
import CartGiftItem from '../cart/CartGiftItem'
import { useCartItems } from '../cart/CartPage/useCartItems'
import Separator from '../layout/Separator'
import Heading from '../typography/Heading'
import { useGetCustomerVouchers } from '../vouchers/useGetCustomerVouchers'

const sortUnclaimedGifts = (a: FreeGiftRule, b: FreeGiftRule) => {
    const amountA = a.insufficientAmount?.value ?? 0
    const amountB = b.insufficientAmount?.value ?? 0
    if (amountA < amountB) {
        return -1
    }
    return 0
}

const renderRule = (rule: FreeGiftRule, withSeparator: boolean) => {
    const { items, products } = rule
    return items?.map((item, index) => (
        <Fragment key={`${index}-${item.productUid}`}>
            {withSeparator && <Separator className={styles.separator} />}
            <CartGiftItem
                productId={item.productId}
                cartItem={item.item}
                rule={rule}
                withSku={false}
                hasGiftSelection={products?.length > 1}
            />
        </Fragment>
    ))
}

export const FreeGifts = () => {
    const isLoggedIn = useIsLoggedIn()

    const { freeGifts: cartRules } = useCartItems()

    const { freeGiftVouchers: srsRules } = useGetCustomerVouchers({
        skipCustomerCoupons: true,
    })

    // If any free gift srs voucher is already in user's cart, filter it away
    const allRules = [
        ...srsRules
            .filter((r) => !r.isCartItem)
            .filter((r) => {
                const srsProductIds = r.items?.map((i) => i.productId)
                const cartRuleProductIds = cartRules
                    ?.map((rule) => rule.items?.map((i) => i.productId))
                    .flat()

                return !cartRuleProductIds?.some((id) =>
                    srsProductIds?.includes(id!),
                )
            }),
        ...(cartRules ?? []),
    ]

    const claimedGiftRules = allRules.filter((rule) => rule.itemIds?.length)

    const unClaimedGiftRules = isLoggedIn
        ? allRules.filter(
              (rule) => !rule.itemIds?.length && rule.isValidCustomerGroup,
          )
        : allRules.filter(
              (rule) =>
                  !rule.isValidCustomerGroup ||
                  (rule.isValidCustomerGroup && !rule.itemIds?.length),
          )

    return (
        <>
            <div className={styles.container}>
                <section className={styles.section}>
                    {claimedGiftRules.length > 0 && (
                        <>
                            <Heading
                                element="h2"
                                variant="h2"
                                className={styles.header}
                            >
                                <Plural
                                    id="cart.gifts.claimed"
                                    value={claimedGiftRules.length}
                                    one="My gift"
                                    other="My gifts"
                                />
                            </Heading>
                        </>
                    )}
                    {claimedGiftRules.map((rule) => renderRule(rule, true))}
                </section>
                <section className={styles.section}>
                    {unClaimedGiftRules.length > 0 && (
                        <>
                            <Heading
                                element="h2"
                                variant="h2"
                                className={styles.header}
                            >
                                {isLoggedIn ? (
                                    <Plural
                                        id="cart.gifts.unclaimed.customer"
                                        value={unClaimedGiftRules.length}
                                        one="Don't miss this gift"
                                        other="Don't miss these gifts"
                                    />
                                ) : (
                                    <Plural
                                        id="cart.gifts.unclaimed.guest"
                                        value={unClaimedGiftRules.length}
                                        one="Don't miss this gift"
                                        other="Don't miss these gifts"
                                    />
                                )}
                            </Heading>
                            {unClaimedGiftRules.length && !isLoggedIn && (
                                <div className={styles.subHeader}>
                                    <Trans id="account.login.headingDefault">
                                        Login or create an account
                                    </Trans>
                                </div>
                            )}
                            <Separator className={styles.separator} />
                        </>
                    )}
                    {unClaimedGiftRules
                        .sort(sortUnclaimedGifts)
                        .map((rule) => renderRule(rule, false))}
                </section>
            </div>
        </>
    )
}
